/**
 * PURPOSE: "Agregar institución" a una serie de una posta de Atletismo: la institución corre como equipo.
 * RESPONSIBILITIES: Listar las instituciones con acreditados de atletismo en el evento (siempre por idinstitution: hay siglas
 *   repetidas) con su situación en la prueba: ya corre (serie y carril: no se agrega, se mueve), tiene un equipo sin serie
 *   (se reutiliza y conserva sus relevistas) o no tiene equipo (se crea sin relevistas; se eligen luego en "Ver relevistas").
 *   Pide la serie (si hay varias) y el carril (LanePicker: libre, o reemplazar / intercambiar al ocupante). Búsqueda local
 *   por sigla o nombre, sin importar tildes. El servidor valida todo otra vez; cualquier rechazo se muestra aquí.
 * COLLABORATORS: PruebaDetailPanel.jsx, LanePicker.jsx, placement.js, utils/athleticsSearch.js.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/AddTeamModal.jsx
 */
import React, { useEffect, useMemo, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import Button from "@mui/joy/Button";
import { FiPlus, FiSearch } from "react-icons/fi";

import { matchesSearchQuery } from "../../../utils/athleticsSearch.js";
import { isRoundLocked, occupantLabel, occupantsOfLane, resolveLaneChoice, roundName } from "../placement.js";
import LanePicker, { EMPTY_LANE_CHOICE } from "./LanePicker.jsx";
import { Badge, Spinner, selectClass } from "./ui.jsx";

const situationBadge = (candidate) => {
  if (candidate.placement) {
    return (
      <Badge tone="green" title="Ya está en la prueba: usa Mover para cambiarlo de serie o carril">
        Ya corre · {candidate.placement.label || "serie"}
        {candidate.placement.lane ? ` · carril ${candidate.placement.lane}` : ""}
      </Badge>
    );
  }
  if (candidate.team) {
    return (
      <Badge tone="blue" title="Se reutiliza su equipo sin serie, con sus relevistas">
        Equipo sin serie{candidate.team.legs ? ` · ${candidate.team.legs} relevistas` : ""}
      </Badge>
    );
  }
  return (
    <Badge tone="slate" title="Se crea el equipo sin relevistas: elígelos después en Ver relevistas">
      Equipo nuevo
    </Badge>
  );
};

const AddTeamModal = ({ open, prueba, rounds, defaultRoundId, preset = null, searchCandidates, onSubmit, onClose }) => {
  const [candidates, setCandidates] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [roundId, setRoundId] = useState(null);
  const [lane, setLane] = useState("");
  const [laneChoice, setLaneChoice] = useState(EMPTY_LANE_CHOICE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const roundList = useMemo(() => (Array.isArray(rounds) ? rounds : []), [rounds]);

  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
    setSelected(null);
    setLane(preset?.lane ? String(preset.lane) : "");
    setLaneChoice(preset?.mode ? { mode: preset.mode, targetRowId: preset.targetRowId ?? null, confirmResult: false } : EMPTY_LANE_CHOICE);
    setSubmitting(false);
    setError("");
    setLoadError("");
    const wanted = preset?.roundId ?? defaultRoundId;
    const initial = roundList.find((round) => round.id === wanted && !isRoundLocked(round)) || roundList.find((round) => !isRoundLocked(round)) || roundList[0];
    setRoundId(initial ? initial.id : null);
    let cancelled = false;
    setCandidates(null);
    Promise.resolve(searchCandidates())
      .then((list) => {
        if (!cancelled) setCandidates(Array.isArray(list) ? list : []);
      })
      .catch((failure) => {
        if (!cancelled) {
          setCandidates([]);
          setLoadError(failure?.response?.data?.message || failure?.message || "No se pudieron cargar las instituciones.");
        }
      });
    return () => {
      cancelled = true;
    };
    // Solo al abrir: los cambios de `rounds` mientras está abierto no deben borrar lo que el operador eligió.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const visible = useMemo(
    () =>
      (candidates || []).filter((candidate) =>
        matchesSearchQuery(`${candidate.institution?.abrev || ""} ${candidate.institution?.name || ""}`, query)
      ),
    [candidates, query]
  );

  const round = roundList.find((item) => item.id === roundId) || null;
  const laneState = resolveLaneChoice({ prueba, round, lane: Number(lane) || null, choice: laneChoice });
  const replacing =
    preset?.mode === "replace" && preset?.targetRowId ? occupantsOfLane(round, preset.lane).find((item) => item.id === preset.targetRowId) : null;
  const missing = !selected
    ? "Elige la institución."
    : !round
      ? "Elige la serie."
      : isRoundLocked(round)
        ? `${roundName(round)} está terminada: cambia su estado para agregar equipos.`
        : !lane
          ? "Elige el carril."
          : laneState.missing;

  const submit = async () => {
    if (missing || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const outcome = await onSubmit({
        institutionId: selected.institution.id,
        roundId: round.id,
        lane: Number(lane),
        ...laneState.params,
        candidate: selected,
        round,
      });
      if (outcome?.ok || outcome?.close) onClose();
      else setError(outcome?.message || "No se pudo agregar.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={Boolean(open)} onClose={submitting ? undefined : onClose}>
      <ModalDialog sx={{ width: "min(96vw, 680px)", maxHeight: "92vh", borderRadius: "16px", overflow: "auto" }}>
        <ModalClose disabled={submitting} />
        <DialogTitle>
          {replacing ? `Reemplazar a ${occupantLabel(replacing)}` : "Agregar institución"} · {prueba?.label || prueba?.name}
        </DialogTitle>
        <DialogContent>
          <p className="text-xs leading-5 text-slate-500">
            La institución corre como equipo. Si ya tiene un equipo sin serie en esta posta se reutiliza con sus relevistas; si no,
            se crea sin relevistas (se eligen después en "Ver relevistas"). Sale en la planilla, FinishLynx y Hayllis.
          </p>

          {!selected ? (
            <>
              <label className="mt-3 flex items-center gap-2 rounded-lg border border-slate-300 px-2 py-1.5 focus-within:border-indigo-400">
                <FiSearch className="shrink-0 text-slate-400" aria-hidden="true" />
                <input
                  type="search"
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Sigla o nombre de la institución"
                  aria-label="Buscar institución"
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                />
                {candidates === null ? <Spinner /> : null}
              </label>
              {loadError ? (
                <p role="alert" className="mt-2 text-xs font-semibold text-rose-700">
                  {loadError}
                </p>
              ) : null}
              {candidates !== null ? (
                visible.length ? (
                  <ul className="mt-2 max-h-72 space-y-1 overflow-y-auto" aria-label="Instituciones">
                    {visible.map((candidate) => {
                      const blocked = Boolean(candidate.placement);
                      return (
                        <li
                          key={candidate.institution.id}
                          className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-800" title={candidate.institution.name}>
                              <span className="font-black">{candidate.institution.abrev || "—"}</span> · {candidate.institution.name}
                            </p>
                            <div className="mt-0.5 flex flex-wrap items-center gap-1">
                              {situationBadge(candidate)}
                              <span className="text-[11px] font-semibold text-slate-400">{candidate.athletes} acreditados</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelected(candidate);
                              setError("");
                            }}
                            disabled={blocked}
                            title={blocked ? "Ya está en la prueba: usa Mover" : undefined}
                            aria-label={`Elegir ${candidate.institution.abrev || candidate.institution.name}`}
                            className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-indigo-50 px-2 py-1 text-[11px] font-bold text-indigo-700 transition hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <FiPlus aria-hidden="true" /> Elegir
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="mt-2 text-xs italic text-slate-500">Ninguna institución con acreditados de atletismo coincide.</p>
                )
              ) : null}
            </>
          ) : (
            <div className="mt-3 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-black text-slate-800">
                    {selected.institution.abrev} · {selected.institution.name}
                  </p>
                  <div className="mt-0.5">{situationBadge(selected)}</div>
                </div>
                <button type="button" onClick={() => setSelected(null)} disabled={submitting} className="text-xs font-bold text-indigo-700 underline">
                  Cambiar
                </button>
              </div>

              {roundList.length > 1 ? (
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Serie</span>
                  <select
                    value={roundId ?? ""}
                    onChange={(event) => {
                      setRoundId(Number(event.target.value));
                      setLane("");
                      setLaneChoice(EMPTY_LANE_CHOICE);
                    }}
                    className={selectClass}
                    aria-label="Serie de destino"
                  >
                    {roundList.map((item) => (
                      <option key={item.id} value={item.id} disabled={isRoundLocked(item)}>
                        {roundName(item)}
                        {isRoundLocked(item) ? " (terminada)" : ""}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <LanePicker prueba={prueba} round={round} lane={lane} onLaneChange={setLane} choice={laneChoice} onChoiceChange={setLaneChoice} />
            </div>
          )}

          {error ? (
            <p role="alert" className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700">
              {error}
            </p>
          ) : null}
          {selected && missing ? <p className="mt-2 text-xs font-semibold text-slate-500">{missing}</p> : null}
        </DialogContent>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="plain" color="neutral" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="solid" color="primary" onClick={submit} disabled={Boolean(missing) || submitting} startDecorator={submitting ? <Spinner /> : null}>
            {submitting ? "Agregando..." : laneState.mode === "replace" ? "Reemplazar" : "Agregar a la serie"}
          </Button>
        </div>
      </ModalDialog>
    </Modal>
  );
};

export default AddTeamModal;
